<template>
    <v-container>
        <AddErrorDialog
            :is-open="addErrorDialog"
            @on-close="closeErrorDialog"
        />

        <v-row>
            <v-col cols="12">
                <v-textarea
                    v-model="complaint.discrepancyCause"
                    label="Przyczyna niezgodności"
                    :disabled="!canSaveComplaint || isComplaintIneditable"
                />
            </v-col>
            <v-col cols="12" lg="8">
                <v-autocomplete
                    v-model="complaint.responsiblePerson"
                    :items="users"
                    :filter="customUsersFilter"
                    :loading="usersSearchLoading"
                    :search-input.sync="usersSearch"
                    label="Osoby odpowiedzialne"
                    class="v-text-field--placeholder-dark"
                    required
                    dense
                    chips
                    deletable-chips
                    cache-items
                    multiple
                    item-value="id"
                    no-data-text="Nie znaleziono"
                    clearable
                    :disabled="!canSaveComplaint || isComplaintIneditable"
                >
                    <template slot="no-data">
                        <v-list-item>
                            <v-list-item-title>
                                <template v-if="!usersSearch">
                                    Wpisz treść aby odnaleźć <strong>osobę odpowiedzialną</strong>.
                                </template>
                                <template v-else-if="usersSearchLoading">
                                    Trwa wyszukiwanie...
                                </template>
                                <template v-else>
                                    Nie znaleziono
                                </template>
                            </v-list-item-title>
                        </v-list-item>
                    </template>
                    <template #selection="data">
                        <template v-if="data.item.user">
                            <template v-if="data.item.user.firstName && data.item.user.lastName">
                                <v-chip
                                    v-if="data.item.confirm"
                                    color="success"
                                    close
                                    @update:active="removeUser(data.item)"
                                >
                                    {{ data.item.user.firstName }} {{ data.item.user.lastName }}
                                </v-chip>
                                <v-chip
                                    v-else
                                    close
                                    @update:active="removeUser(data.item)"
                                >
                                    {{ data.item.user.firstName }} {{ data.item.user.lastName }}
                                </v-chip>
                            </template>
                            <template v-else>
                                <v-chip
                                    v-if="data.item.confirm"
                                    close
                                    color="success"
                                    @update:active="removeUser(data.item)"
                                >
                                    {{ data.item.user.username }}
                                </v-chip>
                                <v-chip
                                    v-else
                                    close
                                    @update:active="removeUser(data.item)"
                                >
                                    {{ data.item.user.username }}
                                </v-chip>
                            </template>
                        </template>
                        <template v-else>
                            <template v-if="data.item.firstName && data.item.lastName">
                                <v-chip
                                    close
                                    @update:active="removeUser(data.item)"
                                >
                                    {{ data.item.firstName }} {{ data.item.lastName }}
                                </v-chip>
                            </template>
                            <template v-else>
                                <v-chip
                                    close
                                    @update:active="removeUser(data.item)"
                                >
                                    {{ data.item.username }}
                                </v-chip>
                            </template>
                        </template>
                    </template>

                    <template #item="data">
                        <template v-if="data.item.user">
                            <template v-if="data.item.user.firstName && data.item.user.lastName">
                                {{ data.item.user.firstName }} {{ data.item.user.lastName }}
                            </template>
                            <template v-else>
                                {{ data.item.user.username }}
                            </template>
                        </template>
                        <template v-else>
                            <template v-if="data.item.firstName && data.item.lastName">
                                {{ data.item.firstName }} {{ data.item.lastName }}
                            </template>
                            <template v-else>
                                {{ data.item.username }}
                            </template>
                        </template>
                    </template>
                </v-autocomplete>
            </v-col>
            <v-col cols="12" lg="5">
                <v-autocomplete
                    v-model="complaint.complaintErrorId"
                    :items="errors"
                    :filter="customErrorFilter"
                    item-value="id"
                    no-data-text="Nie znaleziono opisu błędu"
                    label="Opis błędu"
                    :disabled="!canSaveComplaint || isComplaintIneditable"
                >
                    <template slot="selection" slot-scope="data">
                        {{ data.item.name }}
                    </template>

                    <template slot="item" slot-scope="data">
                        {{ data.item.name }}
                    </template>
                    <template slot="append-outer">
                        <v-tooltip top :open-delay="tooltipDelay">
                            <template #activator="{on}">
                                <v-icon
                                    color="info"
                                    v-on="on"
                                    @click="showAddErrorDialog"
                                >
                                    mdi-plus
                                </v-icon>
                            </template>
                            <span>Dodaj nowy</span>
                        </v-tooltip>
                    </template>
                </v-autocomplete>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import AddErrorDialog from './AddErrorDialog'
import customFilters from '@/mixins/customFilters'
import tooltipDelay from '@/mixins/tooltip'
import { errorHandler, addSentryBreadcrumb } from '@/utils/errorHandler'

let searchTimer

export default {
    name: 'Causes',
    components: {
        AddErrorDialog
    },
    mixins: [ customFilters, tooltipDelay ],
    props: {
        complaint: {
            type: Object,
            default: null
        },
        validations: {
            type: Object,
            default: null
        },
        canSaveComplaint: {
            type: Boolean
        }
    },
    data() {
        return {
            addErrorDialog: false,
            usersSearch: null,
            user: null,
            usersSearchLoading: false,
            users: this.$store.state.complaints.complaints.users,
        }
    },
    computed: {
        squads() {
            return this.$store.state.administration.users.squads
        },
        positions() {
            return this.$store.state.administration.users.positions
        },
        errors() {
            return this.$store.state.complaints.complaints.errors
        },
        userId() {
            return this.$store.state.user.userId
        },
        isDraftComplaint() {
            return this.complaint.complaintStatusId === 15 // robocza
        },
        isComplaintIneditable() {
            return this.isDraftComplaint && (this.complaint.creator.id != this.userId)
        },
    },
    watch: {
        usersSearch(value = null) {
            if (value === null) return

            this.usersSearchLoading = true

            clearTimeout(searchTimer)

            searchTimer = setTimeout(() => {
                if (value) {
                    this.searchUsers(value)
                } else {
                    this.usersSearchLoading = false
                    this.users = []
                }
            }, 500)
        }
    },
    methods: {
        removeUser(user) {
            this.complaint.responsiblePerson.splice(this.complaint.responsiblePerson.indexOf(user.id), 1)
        },
        showAddErrorDialog() {
            this.addErrorDialog = true
        },
        closeErrorDialog() {
            this.addErrorDialog = false
        },
        parsePositionSquad(positionItem, squadItem) {
            if (positionItem === null && squadItem === null) return

            const position = positionItem ? this.positions.find(item => item.id === positionItem.id).name : null,
                squad = squadItem ? this.squads.find(item => item.id === squadItem.id).name : null

            if (position && squad) return `(${position} - ${squad})`
            if (position && !squad) return `(${position})`
            if (!position && squad) return `(${squad})`
        },
        customErrorFilter(item, queryText) {
            const error = item.name.toLowerCase(),
                search = queryText.toLowerCase()

            return error.indexOf(search) > -1
        },
        searchUsers(query = '') {
            addSentryBreadcrumb('ui.action', 'Wyszukiwanie użytkownika')

            this.$store.dispatch('administration/findUsers', {
                paginate: '?limit=20',
                query: query
            })
                .then(response => {
                    this.users = response.data.results
                })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.usersSearchLoading = false
                })
        },
    },
}
</script>